'use client';

import { Portal } from '@ark-ui/react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Button } from '../../../components/styled/button';
import { Text } from '../../../components/styled/text';
import { LabelDefault } from '../../../components/typography';
import { Textarea } from '../../../components/styled/textarea';
import { Input } from '../../../components/styled/input';
import { LegacyDialog } from '../../../components/dialog';
import { IconButton } from '../../../components/icon-button';
import { Field } from '../../../components/field';
import { Select, createSelectListCollection } from '../../../components/select';
import { Tabs } from '../../../components/tabs';
import { Badge } from '../../../components/badge';
import {
  Alert01Icon,
  CheckmarkCircle02Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  PencilEdit02Icon,
  ViewIcon,
  Maximize02Icon,
  Undo02Icon,
  FloppyDiskIcon,
  MagicWand01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { SignalCategorySchema } from '@terra/shared-schemas-orchestrator';
import { MarkdownPreview } from '../../markdown-preview';
import { Tooltip } from '../../../components/tooltip';
import { formatCategoryForDisplay } from '../../../utils/category-utils';

const HITL_MODAL_Z_INDEX = 'toast';

type FindingFormData = {
  title?: string;
  severity?: string;
  category?: string;
  description?: string;
  affectedEndpoint?: string;
  poc?: string;
  businessImpact?: string;
  mitigationPlan?: string;
  endpointMatched?: boolean;
  exploitationGuide?: string;
  executionScript?: string;
  findingNote?: string;
};
type EnhancedEditFindingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (editedFindings: FindingFormData[]) => void;
  findings: FindingFormData[];
  actionName?: string;
  /** When 'inline', renders without Dialog shell for use inside tabs */
  renderMode?: 'dialog' | 'inline';
  allowSubmitWithoutChanges?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
};
type FieldValidation = {
  isValid: boolean;
  message?: string;
};
type PreviewMode = 'split' | 'edit' | 'preview';
const SEVERITY_OPTIONS = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Informative',
] as const;
const severityCollection = createSelectListCollection({
  items: SEVERITY_OPTIONS.map((severity) => ({
    label: severity,
    value: severity,
  })),
});
const categoryCollection = createSelectListCollection({
  items: SignalCategorySchema.options.map((category) => ({
    label: formatCategoryForDisplay(category),
    value: category,
  })),
});

export function EnhancedEditFindingsModal({
  isOpen,
  onClose,
  onSubmit,
  findings,
  actionName,
  renderMode = 'dialog',
  allowSubmitWithoutChanges,
  onApprove,
  onReject,
}: EnhancedEditFindingsModalProps) {
  const t = useTranslations('designSystem.chat.editFindingsModal');
  const tCommon = useTranslations('designSystem.common');
  const [editedFindings, setEditedFindings] = useState<FindingFormData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('split');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFindings, setOriginalFindings] = useState<FindingFormData[]>(
    [],
  );
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (isOpen && findings.length > 0) {
      const findingsCopy = JSON.parse(JSON.stringify(findings));

      setEditedFindings(findingsCopy);
      setOriginalFindings(findingsCopy);
      setCurrentIndex(0);
      setHasUnsavedChanges(false);
    }
  }, [isOpen, findings]);
  const currentFinding = editedFindings[currentIndex];
  const originalFinding = originalFindings[currentIndex];

  useEffect(() => {
    const hasChanges =
      editedFindings.length !== originalFindings.length ||
      editedFindings.some((finding, index) => {
        const original = originalFindings[index];

        if (!original) return true;
        const keys = new Set([
          ...Object.keys(finding),
          ...Object.keys(original),
        ] as Array<keyof FindingFormData>);

        return [...keys].some((key) => !Object.is(finding[key], original[key]));
      });

    setHasUnsavedChanges(hasChanges);
  }, [editedFindings, originalFindings]);
  const validateField = useCallback(
    (
      field: keyof FindingFormData,
      value: string | undefined,
    ): FieldValidation => {
      if (field === 'title' && (!value || value.trim().length === 0)) {
        return { isValid: false, message: 'Title is required' };
      }

      if (field === 'description' && (!value || value.trim().length < 10)) {
        return {
          isValid: false,
          message: 'Description must be at least 10 characters',
        };
      }

      if (field === 'severity' && !value) {
        return { isValid: false, message: 'Severity is required' };
      }

      return { isValid: true };
    },
    [],
  );
  const isFieldModified = useCallback(
    (field: keyof FindingFormData): boolean => {
      return originalFinding?.[field] !== currentFinding?.[field];
    },
    [originalFinding, currentFinding],
  );
  const handleFieldChange = useCallback(
    (field: keyof FindingFormData, value: string | boolean | number) => {
      setEditedFindings((prev) => {
        const updated = [...prev];

        updated[currentIndex] = {
          ...updated[currentIndex],
          [field]: value,
        };

        return updated;
      });
      setHasUnsavedChanges(true);
    },
    [currentIndex],
  );
  const handleResetField = useCallback(
    (field: keyof FindingFormData) => {
      if (originalFinding) {
        const value = originalFinding[field];

        handleFieldChange(
          field,
          value !== undefined && value !== null ? value : '',
        );
      }
    },
    [originalFinding, handleFieldChange],
  );
  const handleResetAll = useCallback(() => {
    if (originalFinding) {
      setEditedFindings((prev) => {
        const updated = [...prev];

        updated[currentIndex] = { ...originalFinding };

        return updated;
      });
    }
  }, [currentIndex, originalFinding]);
  const handleCopyField = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
  }, []);
  const handleSubmit = useCallback(() => {
    if (!hasUnsavedChanges && allowSubmitWithoutChanges && onApprove) {
      onApprove();

      return;
    }

    onSubmit(editedFindings);
    onClose();
  }, [
    allowSubmitWithoutChanges,
    editedFindings,
    hasUnsavedChanges,
    onApprove,
    onSubmit,
    onClose,
  ]);
  const handleClose = useCallback(() => {
    setEditedFindings([]);
    setCurrentIndex(0);
    setHasUnsavedChanges(false);
    onClose();
  }, [onClose]);
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(editedFindings.length - 1, prev + 1));
  }, [editedFindings.length]);
  const getSubmitButtonText = useMemo(() => {
    if (!actionName) return t('submit');
    const lowerName = actionName.toLowerCase();

    if (lowerName.includes('create')) {
      return t('createFindings', { count: findings.length });
    }

    if (lowerName.includes('update')) {
      return t('updateFindings', { count: findings.length });
    }

    return t('submit');
  }, [actionName, findings.length, t]);
  const renderFieldWithPreview = useCallback(
    ({
      field,
      label,
      value,
      placeholder,
      isLarge,
    }: {
      field: keyof FindingFormData;
      label: string;
      value: string;
      placeholder: string;
      isLarge?: boolean;
    }) => {
      const validation = validateField(field, value);
      const isModified = isFieldModified(field);

      return (
        <Field.Root>
          <HStack justifyContent="space-between" alignItems="center" mb={1}>
            <HStack gap={2} alignItems="center">
              <Field.Label fontSize="xs" fontWeight="medium">
                {label}
              </Field.Label>
              {isModified && (
                <Badge variant="emphasis" size="sm">
                  {t('modified')}
                </Badge>
              )}
              {!validation.isValid && (
                <Tooltip title={validation.message}>
                  <Alert01Icon size={14} color="var(--colors-fg-error)" />
                </Tooltip>
              )}
            </HStack>
            <HStack gap={1}>
              {value && (
                <Tooltip title="Copy">
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => handleCopyField(value)}
                    aria-label="Copy field"
                  >
                    <Copy01Icon size={14} />
                  </IconButton>
                </Tooltip>
              )}
              {isModified && (
                <Tooltip title="Reset to original">
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => handleResetField(field)}
                    aria-label="Reset field"
                  >
                    <Undo02Icon size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </HStack>
          </HStack>

          {previewMode === 'split' || previewMode === 'edit' ? (
            isLarge ? (
              <Textarea
                value={value || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                placeholder={placeholder}
                rows={previewMode === 'split' ? 3 : 6}
                resize="vertical"
                size="sm"
              />
            ) : (
              <Input
                size="sm"
                value={value || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                placeholder={placeholder}
              />
            )
          ) : null}

          {(previewMode === 'split' || previewMode === 'preview') &&
            value &&
            isLarge && (
              <Box
                mt={previewMode === 'split' ? 2 : 0}
                p={2}
                bg="bg.subtle"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.default"
              >
                <MarkdownPreview source={value} compact />
              </Box>
            )}
        </Field.Root>
      );
    },
    [
      t,
      previewMode,
      validateField,
      isFieldModified,
      handleFieldChange,
      handleCopyField,
      handleResetField,
    ],
  );

  if (!currentFinding) {
    return null;
  }

  const modifiedFieldsCount = Object.keys(currentFinding).filter((key) =>
    isFieldModified(key as keyof FindingFormData),
  ).length;
  const formContent = (
    <VStack gap={4} padding={4} h="full">
      {/* Header */}
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <VStack alignItems="flex-start" gap={1}>
          <HStack gap={2} alignItems="center">
            <Text fontSize="md" fontWeight="bold">
              {t('title')}
            </Text>
            {hasUnsavedChanges && (
              <Badge variant="emphasis" size="sm">
                {t('fieldsModified', { count: modifiedFieldsCount })}
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="fg.muted">
            {t('description')}
          </Text>
        </VStack>
        <HStack gap={1}>
          {renderMode === 'dialog' && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={() => setIsFullscreen(!isFullscreen)}
                aria-label="Toggle fullscreen"
              >
                <Maximize02Icon size={14} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={t('close')}
            onClick={handleClose}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>
      </HStack>

      {/* Finding Navigation & Controls */}
      <HStack
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        p={2}
        bg="bg.subtle"
        borderRadius="md"
        flexWrap="wrap"
        gap={2}
      >
        <HStack gap={2} alignItems="center">
          {editedFindings.length > 1 && (
            <>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                aria-label={t('previous')}
              >
                <ArrowLeft01Icon size={16} />
              </IconButton>
              <Text
                fontSize="xs"
                fontWeight="medium"
                minW="80px"
                textAlign="center"
              >
                {t('findingCounter', {
                  current: currentIndex + 1,
                  total: editedFindings.length,
                })}
              </Text>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={goToNext}
                disabled={currentIndex === editedFindings.length - 1}
                aria-label={t('next')}
              >
                <ArrowRight01Icon size={16} />
              </IconButton>
            </>
          )}
          {currentFinding.severity && (
            <Badge variant="emphasis">{currentFinding.severity}</Badge>
          )}
        </HStack>

        <HStack gap={1}>
          <Tooltip title="Edit mode">
            <IconButton
              variant={previewMode === 'edit' ? 'solid' : 'ghost'}
              size="xs"
              onClick={() => setPreviewMode('edit')}
              aria-label="Edit mode"
            >
              <PencilEdit02Icon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Split view">
            <IconButton
              variant={previewMode === 'split' ? 'solid' : 'ghost'}
              size="xs"
              onClick={() => setPreviewMode('split')}
              aria-label="Split view"
            >
              <MagicWand01Icon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview mode">
            <IconButton
              variant={previewMode === 'preview' ? 'solid' : 'ghost'}
              size="xs"
              onClick={() => setPreviewMode('preview')}
              aria-label="Preview mode"
            >
              <ViewIcon size={14} />
            </IconButton>
          </Tooltip>
          {hasUnsavedChanges && (
            <Tooltip title="Reset all fields">
              <Button variant="ghost" size="xs" onClick={handleResetAll}>
                <Undo02Icon size={14} />
                {t('resetAll')}
              </Button>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {/* Form Fields with Tabs */}
      <Tabs.Root
        defaultValue="details"
        variant="enclosed"
        width="100%"
        flex={1}
        overflow="hidden"
      >
        <Tabs.List>
          <Tabs.Trigger value="details">
            <HStack gap={2}>
              <Text>{t('tabs.details')}</Text>
              {(isFieldModified('title') ||
                isFieldModified('severity') ||
                isFieldModified('category') ||
                isFieldModified('affectedEndpoint')) && (
                <CheckmarkCircle02Icon size={12} color="var(--colors-blue-9)" />
              )}
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="description">
            <HStack gap={2}>
              <Text>{t('tabs.descriptionAndPoc')}</Text>
              {(isFieldModified('description') || isFieldModified('poc')) && (
                <CheckmarkCircle02Icon size={12} color="var(--colors-blue-9)" />
              )}
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="impact">
            <HStack gap={2}>
              <LabelDefault>{t('tabs.impactAndMitigation')}</LabelDefault>
              {(isFieldModified('businessImpact') ||
                isFieldModified('mitigationPlan')) && (
                <CheckmarkCircle02Icon size={12} color="var(--colors-blue-9)" />
              )}
            </HStack>
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="details" p={0} pt={3} flex={1} overflow="auto">
          <VStack gap={3} width="100%" paddingRight={2} alignItems="stretch">
            {renderFieldWithPreview({
              field: 'title',
              label: t('fields.title'),
              value: currentFinding.title || '',
              placeholder: t('placeholders.title'),
            })}

            <HStack gap={3} width="100%" alignItems="flex-start">
              <Field.Root flex={1}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('fields.severity')}
                </Field.Label>
                <Select.Root
                  positioning={{ sameWidth: true, strategy: 'fixed' }}
                  size="sm"
                  collection={severityCollection}
                  value={
                    currentFinding.severity ? [currentFinding.severity] : []
                  }
                  onValueChange={(e) =>
                    handleFieldChange('severity', e.value[0] || '')
                  }
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText
                        placeholder={t('placeholders.severity')}
                      />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      <Select.ItemGroup>
                        {severityCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            <Select.ItemText>{item.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.ItemGroup>
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Field.Root>

              <Field.Root flex={1}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('fields.category')}
                </Field.Label>
                <Select.Root
                  positioning={{ sameWidth: true, strategy: 'fixed' }}
                  size="sm"
                  collection={categoryCollection}
                  value={
                    currentFinding.category ? [currentFinding.category] : []
                  }
                  onValueChange={(e) =>
                    handleFieldChange('category', e.value[0] || '')
                  }
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText
                        placeholder={t('placeholders.category')}
                      />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      <Select.ItemGroup>
                        {categoryCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            <Select.ItemText>{item.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.ItemGroup>
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Field.Root>
            </HStack>

            {renderFieldWithPreview({
              field: 'affectedEndpoint',
              label: t('fields.affectedEndpoint'),
              value: currentFinding.affectedEndpoint || '',
              placeholder: t('placeholders.affectedEndpoint'),
            })}
          </VStack>
        </Tabs.Content>

        <Tabs.Content value="description" p={0} pt={3} flex={1} overflow="auto">
          <VStack gap={3} width="100%" paddingRight={2} alignItems="stretch">
            {renderFieldWithPreview({
              field: 'description',
              label: t('fields.description'),
              value: currentFinding.description || '',
              placeholder: t('placeholders.description'),
              isLarge: true,
            })}

            {renderFieldWithPreview({
              field: 'poc',
              label: t('fields.poc'),
              value: currentFinding.poc || '',
              placeholder: t('placeholders.poc'),
              isLarge: true,
            })}
          </VStack>
        </Tabs.Content>

        <Tabs.Content value="impact" p={0} pt={3} flex={1} overflow="auto">
          <VStack gap={3} width="100%" paddingRight={2} alignItems="stretch">
            {renderFieldWithPreview({
              field: 'businessImpact',
              label: t('fields.businessImpact'),
              value: currentFinding.businessImpact || '',
              placeholder: t('placeholders.businessImpact'),
              isLarge: true,
            })}

            {renderFieldWithPreview({
              field: 'mitigationPlan',
              label: t('fields.mitigationPlan'),
              value: currentFinding.mitigationPlan || '',
              placeholder: t('placeholders.mitigationPlan'),
              isLarge: true,
            })}
          </VStack>
        </Tabs.Content>
      </Tabs.Root>

      {/* Footer Actions */}
      <HStack
        gap={2}
        justifyContent="space-between"
        width="100%"
        pt={2}
        borderTopWidth="1px"
        borderColor="border.default"
      >
        <Text fontSize="xs" color="fg.muted">
          {hasUnsavedChanges
            ? t('fieldsModified', { count: modifiedFieldsCount })
            : t('noChanges')}
        </Text>
        <HStack gap={2}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (onReject) {
                onReject();

                return;
              }

              handleClose();
            }}
            colorPalette={onReject ? 'red' : undefined}
          >
            {onReject ? tCommon('reject') : t('cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!allowSubmitWithoutChanges && !hasUnsavedChanges}
          >
            <FloppyDiskIcon size={14} />
            {getSubmitButtonText}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );

  if (renderMode === 'inline') {
    return formContent;
  }

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && handleClose()}
      lazyMount
    >
      <Portal>
        <LegacyDialog.Backdrop zIndex={HITL_MODAL_Z_INDEX} />
        <LegacyDialog.Positioner zIndex={HITL_MODAL_Z_INDEX}>
          <LegacyDialog.Content
            maxWidth={isFullscreen ? '95vw' : '900px'}
            width="90vw"
            maxHeight={isFullscreen ? '95vh' : '90vh'}
          >
            {formContent}
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </Portal>
    </LegacyDialog.Root>
  );
}
